import { c } from '../../intl'
import { ApiRound } from '../../round/models/ApiRound'
import { Round } from '../../round/models/Round'
import {
  createFieldChoice,
  createFieldImageChoice,
  createFieldNa,
  createFieldNone,
  createFieldNps,
  createFieldOther,
  createFieldRanked,
  createFieldSkip,
  createFieldText,
  createFieldVideo,
} from '../factories/Field'
import {
  createQuestionChoice,
  createQuestionImageChoice,
  createQuestionNps,
  createQuestionOpenText,
  createQuestionRanked,
  createQuestionVideo,
} from '../factories/Question'
import { ApiQuestion, ApiQuestionAnswer } from '../models/ApiQuestion'
import { FieldSpecial } from '../models/Field'
import { Question } from '../models/Question'

export const transformApiQuestion = (round: Round, apiQuestion: ApiQuestion): Question[] => {
  switch (apiQuestion.type) {
    case 'text_response_long':
    case 'text_response_medium':
    case 'text_response_short':
      return [transformApiOpenTextQuestion(round, apiQuestion)]
    case 'video':
      return [transformApiVideoQuestion(round, apiQuestion)]
    case 'nps':
      return [transformApiNpsQuestion(round, apiQuestion)]
    case 'single_choice':
    case 'multiple_choice':
      return [transformApiChoiceQuestion(round, apiQuestion)]
    case 'ranked':
      return [transformApiRankedQuestion(round, apiQuestion)]
    case 'grid':
      return transformApiGridQuestion(round, apiQuestion)
    case 'max_diff':
      return transformApiMaxDiffQuestion(apiQuestion)
  }
}

const transformApiQuestionCommon = (
  apiQuestion: Pick<ApiQuestion, 'id' | 'title' | 'title-content'>,
): Pick<Question, 'id' | 'title' | 'media'> => {
  return {
    id: apiQuestion.id,
    title: apiQuestion.title,
    ...(() => {
      const apiTitleContent = apiQuestion['title-content']
      const muxMedia = getMuxMediaFromTitleContent(apiTitleContent)
      if (muxMedia) {
        return { media: muxMedia }
      }

      const type = (['audio', 'image', 'video'] as const).find(
        mediaType => !!apiTitleContent[`${mediaType}-url`],
      )
      if (!type) return
      const url = apiTitleContent[`${type}-url`]
      if (!url) throw new Error('expect url to be defined')
      return { media: { url, type } }
    })(),
  }
}

const getMuxMediaFromTitleContent = (
  titleContent: Pick<ApiQuestion, 'title-content'>['title-content'],
): Pick<Question, 'media'>['media'] | undefined => {
  const mux = titleContent.mux
  if (!mux) return undefined

  return {
    playbackId: mux['playback-id'],
    playbackToken: mux['playback-token'] ?? '',
    thumbnailToken: mux['thumbnail-token'] ?? '',
    type: 'mux',
  }
}

export const OPEN_QUESTION_TEXT_CHARACTER_LIMITS = {
  text_response_short: 60,
  text_response_medium: 140,
  text_response_long: 800,
} as const

const transformApiOpenTextQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<`text_response_${'short' | 'medium' | 'long'}`>,
): Question<'openText'> =>
  createQuestionOpenText({
    ...transformApiQuestionCommon(apiQuestion),
    fields: [
      ...apiQuestion.answers.map(answer =>
        createFieldText({ id: answer.id, title: answer.text ?? '' }),
      ),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
    characterLimit: OPEN_QUESTION_TEXT_CHARACTER_LIMITS[apiQuestion.type],
  })

const transformApiVideoQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'video'>,
): Question<'video'> => {
  const [videoAnswer] = apiQuestion.answers
  const [objectStorage] = apiQuestion['object-storage']
  if (videoAnswer?.type !== 'text') throw new Error('expect video question to have text answer')
  if (!objectStorage?.url || !objectStorage?.id)
    throw new Error('expected object storage to have upload url and video id')

  return createQuestionVideo({
    ...transformApiQuestionCommon(apiQuestion),
    prompt: videoAnswer.text ?? '',
    fields: [
      createFieldVideo({
        id: videoAnswer.id,
      }),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
  })
}

const transformApiNpsQuestion = (round: Round, apiQuestion: ApiQuestion<'nps'>): Question<'nps'> =>
  createQuestionNps({
    ...transformApiQuestionCommon(apiQuestion),
    fields: [
      createFieldNps({
        id: 'nps',
        ratingToApiAnswerIdMap: new Map(apiQuestion.answers.map((answer, i) => [i, answer.id])),
      }),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
  })

const transformApiChoiceQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'single_choice' | 'multiple_choice'>,
): Question<'choice'> | Question<'imageChoice'> => {
  const partialChoiceQuestion: Omit<Question<'choice' | 'imageChoice'>, 'fields' | 'type'> = {
    ...transformApiQuestionCommon(apiQuestion),
    maxAnswers:
      apiQuestion.type === 'single_choice'
        ? 1
        : (apiQuestion.max_selections ?? Number.POSITIVE_INFINITY),
  }

  return {
    text: () =>
      createQuestionChoice({
        ...partialChoiceQuestion,
        fields: [
          ...apiQuestion.answers.map(answer =>
            createFieldChoice({ id: answer.id, title: answer.text ?? '' }),
          ),
          ...(apiQuestion['has-other'] ? [createFieldOther()] : []),
          ...createSpecialFields({
            ...apiQuestion,
            'skip-disabled': round.skippedDisabled,
          }),
        ],
        subject: apiQuestion['subject-title'],
      }),
    image: () =>
      createQuestionImageChoice({
        ...partialChoiceQuestion,
        fields: [
          ...apiQuestion.answers.map(answer => {
            if (answer.type !== 'image') throw new Error('expect all api answers are of type image')
            return createFieldImageChoice({
              id: answer.id,
              url: answer.url,
              description: answer.text,
            })
          }),
          ...createSpecialFields({
            ...apiQuestion,
            'skip-disabled': round.skippedDisabled,
          }),
        ],
        display: apiQuestion['image-answer-display-size'],
        subject: apiQuestion['subject-title'],
      }),
  }[getChoiceQuestionType(apiQuestion)]()
}

const transformApiRankedQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'ranked'>,
): Question<'ranked'> =>
  createQuestionRanked({
    ...transformApiQuestionCommon(apiQuestion),
    fields: [
      ...apiQuestion.answers.map(answer =>
        createFieldRanked({ id: answer.id, title: answer.text ?? '' }),
      ),
      ...createSpecialFields({
        ...apiQuestion,
        'skip-disabled': round.skippedDisabled,
      }),
    ],
  })

const transformApiGridQuestion = (
  round: Round,
  apiQuestion: ApiQuestion<'grid'>,
): Question<'choice' | 'imageChoice'>[] => {
  return apiQuestion.answers.subjects.headers.map((subjectHeader, subjectIndex) => {
    const apiChoiceQuestionPartial = {
      'has-na': apiQuestion.answers.options.na,
      'has-none': apiQuestion.answers.options.none,
      'has-other': apiQuestion['has-other'],
      'image-answer-display-size': apiQuestion['image-answer-display-size'],
      'is-randomized': apiQuestion['is-randomized'],
      'title-content': apiQuestion['title-content'],
      id: apiQuestion.id,
      title: apiQuestion.title,
      answers: apiQuestion.answers.options.headers.map(
        (optionHeader, optionIndex): ApiQuestionAnswer => {
          const answerId = apiQuestion.answers.choices[subjectIndex]?.values[optionIndex]?.id
          if (!answerId)
            throw Error(
              `Could not find field id for option '${optionHeader.text}' in ${apiQuestion.answers.choices[subjectIndex]}`,
            )

          if (optionHeader.type === 'text')
            return {
              id: answerId,
              type: optionHeader.type,
              text: optionHeader.text,
            }
          if (optionHeader.type === 'image')
            return {
              id: answerId,
              type: optionHeader.type,
              text: optionHeader.text,
              url: optionHeader.url,
            }

          throw Error(`unrecognised optionHeader type`)
        },
      ),
    }

    return {
      ...transformApiChoiceQuestion(
        round,
        {
          single_choice: {
            ...apiChoiceQuestionPartial,
            type: 'single_choice' as const,
          },
          multiple_choice: {
            ...apiChoiceQuestionPartial,
            type: 'multiple_choice' as const,
            max_selections: apiQuestion.answers.options.max_selections,
          },
        }[apiQuestion.answers.options.type],
      ),
      subject: subjectHeader.text,
      ...(subjectHeader.type === 'image' ? { subjectImage: subjectHeader.url } : {}),
    }
  })
}

const transformApiMaxDiffQuestion = (
  apiQuestion: ApiQuestion<'max_diff'>,
): Question<'maxDiffSet'>[] => {
  return Object.entries(apiQuestion.answers).map(([setId, set], setIndex, sets) => ({
    ...transformApiQuestionCommon(apiQuestion),
    type: 'maxDiffSet' as const,
    fields: set.map(answer => ({
      id: `${setId}-${answer.id}`,
      setId,
      answerId: answer.id,
      type: 'maxDiffSetChoice',
      title: answer.text,
    })),
    bestLabel: apiQuestion.labels.best,
    worstLabel: apiQuestion.labels.worst,
    subject: c('max_diff.set_subtitle', {
      placeholders: {
        setNumber: setIndex + 1,
        numberOfSets: sets.length,
      },
    }),
  }))
}

export const createSpecialFields = (
  options: Pick<ApiQuestion, 'has-na' | 'has-none'> & Pick<ApiRound, 'skip-disabled'>,
): FieldSpecial[] => {
  return [
    ...(options['has-none'] ? [createFieldNone()] : []),
    ...(options['has-na'] ? [createFieldNa()] : []),
    ...(options['skip-disabled'] ? [] : [createFieldSkip()]),
  ]
}

const getChoiceQuestionType = (
  apiQuestion: ApiQuestion<'single_choice' | 'multiple_choice'>,
): 'text' | 'image' => {
  if (apiQuestion.answers.every(({ type }) => type === 'text')) return 'text'
  if (apiQuestion.answers.every(({ type }) => type === 'image')) return 'image'
  throw new Error('do not expect a mixture of image and text answers')
}
